import { createContext, useContext, useEffect, useRef, useState } from 'react';

import { computeVariant, type Config } from './index';

// From userjourney

interface PostHogInstance {
  get_distinct_id(): string;
  capture(event: string, properties: Record<string, unknown>): void;
}

interface ABContextValue {
  config: Config | null;
  configLoaded: boolean;
  posthog: PostHogInstance | null;
  userId: string | null;
  exposed: Set<string>;
  overrides: Map<string, string>;
}

const defaultABContextValue: ABContextValue = {
  config: null,
  configLoaded: false,
  posthog: null,
  userId: null,
  exposed: new Set(),
  overrides: new Map(),
};

export const ABContext = createContext<ABContextValue>(defaultABContextValue);

// Second context for the user-id-bucketed provider
// (src/providers/ab-config-authed.tsx). Coexists with ABContext during the
// transition period while live experiments stay on distinct_id bucketing.
// That provider defines its own hooks that read from this context.
export const ABContextAuthed = createContext<ABContextValue>(defaultABContextValue);

const OVERRIDES_STORAGE_KEY = 'ab_variant_overrides';

function loadOverrides(): Map<string, string> {
  try {
    const raw = localStorage.getItem(OVERRIDES_STORAGE_KEY);
    if (!raw) return new Map();
    return new Map(Object.entries(JSON.parse(raw)));
  } catch {
    return new Map();
  }
}

function saveOverrides(overrides: Map<string, string>) {
  localStorage.setItem(
    OVERRIDES_STORAGE_KEY,
    JSON.stringify(Object.fromEntries(overrides)),
  );
}

export function setVariantOverride(experimentKey: string, variant: string) {
  const overrides = loadOverrides();
  overrides.set(experimentKey, variant);
  saveOverrides(overrides);
  window.dispatchEvent(new Event('ab-overrides-changed'));
}

export function clearVariantOverride(experimentKey: string) {
  const overrides = loadOverrides();
  overrides.delete(experimentKey);
  saveOverrides(overrides);
  window.dispatchEvent(new Event('ab-overrides-changed'));
}

export function getVariantOverrides(): Map<string, string> {
  return loadOverrides();
}

export function ABProvider({
  config,
  configLoaded,
  posthog,
  userId,
  children,
}: {
  config: Config | null;
  configLoaded: boolean;
  posthog: PostHogInstance | null;
  userId: string | null;
  children: React.ReactNode;
}) {
  const exposed = useRef(new Set<string>());
  const [overrides, setOverrides] = useState(() => loadOverrides());

  useEffect(() => {
    const handler = () => setOverrides(loadOverrides());
    window.addEventListener('ab-overrides-changed', handler);
    return () => window.removeEventListener('ab-overrides-changed', handler);
  }, []);

  return (
    <ABContext.Provider
      value={{
        config,
        configLoaded,
        posthog,
        userId,
        exposed: exposed.current,
        overrides,
      }}>
      {children}
    </ABContext.Provider>
  );
}

export function useABConfigLoaded(): boolean {
  return useContext(ABContext).configLoaded;
}

export function useVariant(
  experimentKey: string,
  userAttributes?: Record<string, unknown>,
): string | null {
  const { config, posthog, userId, exposed, overrides } = useContext(ABContext);

  const override = overrides.get(experimentKey);

  let variant: string | null = null;
  if (!override && config && userId) {
    variant = computeVariant(userId, experimentKey, config, userAttributes);
  }

  useEffect(() => {
    if (override) return;
    if (!variant || !posthog || !config || !userId) return;
    const dedupKey = `${experimentKey}:${variant}:${config.version}`;
    if (exposed.has(dedupKey)) return;
    exposed.add(dedupKey);
    const exp = config.experiments.find((e) => e.key === experimentKey);
    posthog.capture('$experiment_exposure', {
      $experiment_key: experimentKey,
      $variant: variant,
      $layer: exp?.layer ?? '',
    });
  }, [variant, override, experimentKey, posthog, config, userId, exposed]);

  return override ?? variant;
}
